import * as React from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";

import Logo from "@/assets/favicon.ico";

import { NavMain, type NavSection } from "@/components/nav-main";
import { NavUser } from "@/components/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useAuth } from "@/hooks/useAuth";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { alert } from "@/contexts/AlertModalContext";

import { MENU_SECTIONS, PROTECTED_MENU_URLS } from "@/config/menuSections";

import { Copy } from "lucide-react";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation();
  const { usuario } = useAuth();
  const { impressaoConfig } = useImpressaoConfig();

  // Botões que o admin optou por ocultar para o cliente (salvo no impressao_config).
  // Botões protegidos nunca são escondidos, mesmo que apareçam na lista.
  const botoesOcultos = new Set(impressaoConfig?.menu_botoes_ocultos ?? []);

  const sections: NavSection[] = MENU_SECTIONS.map((section) => ({
    label: t(section.labelKey),
    items: section.items
      .filter(
        (item) =>
          PROTECTED_MENU_URLS.includes(item.url) || !botoesOcultos.has(item.url),
      )
      .map((item) => ({
        title: t(item.titleKey),
        url: item.url,
        icon: item.icon,
      })),
  })).filter((section) => section.items.length > 0);

  const copiarIds = () => {
    const texto = [
      `id_conta: ${usuario?.id_conta ?? "-"}`,
      `id_usuario: ${usuario?._id ?? "-"}`,
      `id_impressao_config: ${impressaoConfig?._id ?? "-"}`,
    ].join("\n");
    navigator.clipboard.writeText(texto);
    alert.success(t("nav.idsCopied"));
  };

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:!p-1.5"
            >
              <Link to="/" className="flex items-center gap-2">
                <img src={Logo} alt="Logo" className="h-6 w-6 rounded" />
                <div className="flex flex-col min-w-0 leading-tight">
                  <span className="text-sm font-semibold truncate">
                    {t("brand.title")}
                  </span>
                  <span className="text-[10px] text-muted-foreground truncate">
                    iNOVACODE
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <NavMain sections={sections} />
      </SidebarContent>

      <SidebarFooter>
        {/* IDs de debug — discretos por padrão, ficam visíveis no hover.
            Útil pro suporte/dev; o usuário final basicamente não percebe. */}
        <TooltipProvider delayDuration={500}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={copiarIds}
                className="group w-full text-left px-2 py-1 rounded opacity-20 hover:opacity-100 transition-opacity flex items-center gap-1.5"
                aria-label={t("nav.copyIds")}
              >
                <Copy className="h-2.5 w-2.5 text-muted-foreground shrink-0" />
                <span className="text-[9px] text-muted-foreground/60 truncate flex-1">
                  {usuario?.id_conta ?? "-"} · {impressaoConfig?._id ?? "-"}
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {t("nav.copyIdsTooltip")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
